import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";

export type PlanType = "free" | "premium" | "pro";

export interface Subscription {
  plan_type: PlanType;
  status: string;
  current_period_end: string | null;
}

export const useSubscription = () => {
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSubscription();

    const channel = supabase
      .channel("subscription-changes")
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "subscriptions",
        },
        () => {
          fetchSubscription();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  const fetchSubscription = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        setSubscription({ plan_type: "free", status: "active", current_period_end: null });
        setLoading(false);
        return;
      }

      const { data, error } = await supabase
        .from("subscriptions")
        .select("plan_type, status, current_period_end")
        .eq("user_id", user.id)
        .single();

      if (error || !data) {
        setSubscription({ plan_type: "free", status: "active", current_period_end: null });
      } else {
        setSubscription({
          plan_type: data.plan_type as PlanType,
          status: data.status,
          current_period_end: data.current_period_end,
        });
      }
    } catch (error) {
      console.error("Error fetching subscription:", error);
      setSubscription({ plan_type: "free", status: "active", current_period_end: null });
    } finally {
      setLoading(false);
    }
  };

  const isPremium = subscription?.plan_type === "premium" || subscription?.plan_type === "pro";
  const isPro = subscription?.plan_type === "pro";
  const isFree = subscription?.plan_type === "free";

  return {
    subscription,
    loading,
    isPremium,
    isPro,
    isFree,
    refetch: fetchSubscription,
  };
};
